import { SYNC_PULL_REFERENCE_KEYS } from '@database/catalogRowHelpers.mjs';
import { AppConfig } from '@core/domain/AppConfig.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import { emitAuthSessionExpired } from '@core/domain/sessionEvents.ts';
import { readResponseJsonWithProgress } from '@core/domain/utils/readResponseJsonWithProgress.ts';
import type { SyncPullOptions, SyncPullPort, SyncPullResult } from '../../domain/ports/SyncPorts.ts';
import {
  normalizeCatalogPullSections,
  parseCatalogPullEnvelope,
} from '@src/modules/platform/sync/domain/catalogPullResponse.ts';

type PullResponse = {
  unidadPrograma?: string;
} & Record<string, unknown>;

function isUnauthorizedPullBody(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const record = raw as Record<string, unknown>;
  return record.status === false && record.code === 401;
}

/** Pull-only sync against configured catalog URL (mock `/sync/pull` or Inclusion Social). */
export class ApiSyncPullAdapter implements SyncPullPort {
  constructor(
    private readonly getToken: () => string | null,
    private readonly apiBaseUrl: string = AppConfig.getApiBaseUrl(),
  ) {}

  async pull(opts?: SyncPullOptions): Promise<SyncPullResult> {
    const url = AppConfig.getCatalogPullUrl(this.apiBaseUrl);
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Idempotency-Key': createCorrelationId(),
    };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, { headers, cache: 'no-store' });
    if (!response.ok) {
      if (response.status === 401) {
        emitAuthSessionExpired();
        throw new Error('Sesión expirada. Vuelva a iniciar sesión.');
      }
      if (response.status === 404) {
        throw new Error('El servidor aún no permite descargar catálogos.');
      }
      throw new Error(`No se pudieron descargar catálogos (código ${response.status}).`);
    }

    let raw: unknown;
    try {
      raw = opts?.onDownloadProgress
        ? await readResponseJsonWithProgress(response, opts.onDownloadProgress)
        : await response.json();
    } catch {
      throw new Error('No se pudieron descargar catálogos: respuesta inválida del servidor.');
    }

    if (isUnauthorizedPullBody(raw)) {
      emitAuthSessionExpired();
      throw new Error('Sesión expirada. Vuelva a iniciar sesión.');
    }

    const inner = normalizeCatalogPullSections(parseCatalogPullEnvelope(raw)) as PullResponse;

    const sections: Record<string, unknown> = {};
    for (const key of SYNC_PULL_REFERENCE_KEYS as readonly string[]) {
      const rows = inner[key];
      if (Array.isArray(rows) && rows.length > 0) sections[key] = rows;
    }
    if (typeof inner.unidadPrograma === 'string' && inner.unidadPrograma.trim()) {
      sections.unidadPrograma = inner.unidadPrograma.trim();
    }

    return {
      sections: Object.keys(sections).length > 0 ? sections : undefined,
    };
  }
}
